import React, { useMemo, useState } from "react";

const fmt = (v, d = 2) =>
  Number(v || 0).toLocaleString("ru-RU", {
    minimumFractionDigits: d,
    maximumFractionDigits: d,
  });

export default function PackagingCalculator({ components }) {
  const [productQty, setProductQty] = useState(100);

  const rows = useMemo(() => {
    const q = Number(productQty || 0);
    if (!q) return [];

    return components.map((c) => {
      const needUnits = q * Number(c.quantity_in_product || 0);
      const packagings = (c.packagings || []).map((p) => {
        const perBox = Number(p.quantity || 0);
        const boxes = perBox > 0 ? Math.ceil(needUnits / perBox) : 0;
        const gross = boxes * Number(p.gross_weight || 0);
        const net = boxes * Number(p.net_weight || 0);
        return {
          ...p,
          needUnits,
          boxes,
          grossTotal: gross,
          netTotal: net,
          tareTotal: gross - net,
          volumeTotal: boxes * Number(p.volume_m3 || 0),
        };
      });
      return { component: c, packagings };
    });
  }, [components, productQty]);

  const totals = useMemo(() => {
    let boxes = 0,
      gross = 0,
      net = 0,
      tare = 0,
      volume = 0;
    rows.forEach((r) =>
      r.packagings.forEach((p) => {
        boxes += p.boxes;
        gross += p.grossTotal;
        net += p.netTotal;
        tare += p.tareTotal;
        volume += p.volumeTotal;
      })
    );
    return { boxes, gross, net, tare, volume };
  }, [rows]);

  if (!components.length) return null;

  return (
    <div className="mt-6 p-4 border rounded-xl bg-slate-50">
      <h3 className="text-lg font-bold mb-1">Калькулятор packing list</h3>
      <p className="text-xs text-gray-500 mb-3">
        Введите кол-во продуктов — калькулятор посчитает, сколько нужно упаковок
        каждой составной части и общий вес/объём.
      </p>

      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">
          Кол-во продуктов (шт)
        </label>
        <input
          type="number"
          min="1"
          step="1"
          value={productQty}
          onChange={(e) => setProductQty(e.target.value)}
          className="w-full sm:w-64 p-2 border rounded"
        />
      </div>

      <div className="space-y-4">
        {rows.map(({ component, packagings }) => (
          <div key={component.id} className="border rounded-lg overflow-hidden">
            <div className="bg-white px-3 py-2 font-semibold text-sm">
              {component.name}{" "}
              <span className="text-gray-500 font-normal">
                × {Number(component.quantity_in_product || 0)} на продукт → нужно{" "}
                {fmt(packagings[0]?.needUnits ?? 0, 0)} шт
              </span>
            </div>
            {packagings.length === 0 ? (
              <div className="px-3 py-2 text-xs text-gray-500">
                нет упаковок
              </div>
            ) : (
              <table className="w-full text-xs">
                <thead className="bg-gray-100 text-left">
                  <tr>
                    <th className="p-2">Упаковка</th>
                    <th className="p-2">Размер</th>
                    <th className="p-2">В уп.</th>
                    <th className="p-2">Упаковок</th>
                    <th className="p-2">Брутто</th>
                    <th className="p-2">Нетто</th>
                    <th className="p-2">Тара</th>
                    <th className="p-2">Объём</th>
                  </tr>
                </thead>
                <tbody>
                  {packagings.map((p) => (
                    <tr key={p.id} className="border-t">
                      <td className="p-2">{p.name}</td>
                      <td className="p-2 whitespace-nowrap">
                        {p.length}×{p.width}×{p.height} см
                      </td>
                      <td className="p-2">{p.quantity}</td>
                      <td className="p-2 font-semibold">{p.boxes}</td>
                      <td className="p-2">{fmt(p.grossTotal, 3)} кг</td>
                      <td className="p-2">{fmt(p.netTotal, 3)} кг</td>
                      <td className="p-2">{fmt(p.tareTotal, 3)} кг</td>
                      <td className="p-2">{fmt(p.volumeTotal, 4)} м³</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ))}
      </div>

      {totals.boxes > 0 && (
        <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3">
          <Stat label="Всего упаковок" value={`${totals.boxes} шт`} />
          <Stat label="Общий брутто" value={`${fmt(totals.gross, 3)} кг`} />
          <Stat label="Общий нетто" value={`${fmt(totals.net, 3)} кг`} />
          <Stat label="Тара" value={`${fmt(totals.tare, 3)} кг`} />
          <Stat label="Объём" value={`${fmt(totals.volume, 4)} м³`} />
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg bg-white p-3 border">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="font-semibold mt-1">{value}</div>
    </div>
  );
}